import { useState } from 'react'
import {
  Globe,
  Plus,
  Shield,
  Wifi,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Key,
  Trash2,
  Edit2,
} from 'lucide-react'
import styles from './CloudflarePage.module.css'

type RecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT' | 'NS' | 'SRV'

interface DNSRecord {
  id: string
  type: RecordType
  name: string
  value: string
  ttl: number | 'auto'
  proxied: boolean
}

interface Zone {
  id: string
  name: string
  status: 'active' | 'pending'
  records: DNSRecord[]
}

const ZONES: Zone[] = [
  {
    id: 'zone-1',
    name: 'aptean.app',
    status: 'active',
    records: [
      { id: 'r-001', type: 'A',     name: '@',             value: '20.42.100.10',          ttl: 'auto', proxied: true  },
      { id: 'r-002', type: 'A',     name: 'api',           value: '20.42.100.10',          ttl: 'auto', proxied: true  },
      { id: 'r-003', type: 'A',     name: 'mcp',           value: '20.42.100.11',          ttl: 'auto', proxied: true  },
      { id: 'r-004', type: 'CNAME', name: 'www',           value: 'aptean.app',            ttl: 'auto', proxied: true  },
      { id: 'r-005', type: 'CNAME', name: 'demo',           value: 'demo.aptean.app',       ttl: 3600,   proxied: true  },
      { id: 'r-006', type: 'MX',    name: '@',             value: 'mail.aptean.com',       ttl: 3600,   proxied: false },
      { id: 'r-007', type: 'TXT',   name: '@',             value: 'v=spf1 include:...',    ttl: 3600,   proxied: false },
      { id: 'r-008', type: 'TXT',   name: '_dmarc',        value: 'v=DMARC1; p=reject;...',ttl: 3600,   proxied: false },
    ],
  },
  {
    id: 'zone-2',
    name: 'aptean.com',
    status: 'active',
    records: [
      { id: 'r-010', type: 'A',     name: '@',             value: '20.42.200.5',           ttl: 'auto', proxied: true  },
      { id: 'r-011', type: 'CNAME', name: 'www',           value: 'aptean.com',            ttl: 'auto', proxied: true  },
      { id: 'r-012', type: 'MX',    name: '@',             value: 'mail.aptean.com',       ttl: 3600,   proxied: false },
      { id: 'r-013', type: 'TXT',   name: '@',             value: 'v=spf1 include:...',    ttl: 3600,   proxied: false },
      { id: 'r-014', type: 'A',     name: 'docs',          value: '20.42.200.10',          ttl: 3600,   proxied: true  },
    ],
  },
  {
    id: 'zone-3',
    name: 'erpcloud.aptean.com',
    status: 'active',
    records: [
      { id: 'r-020', type: 'A',     name: '@',             value: '20.42.300.1',           ttl: 'auto', proxied: true  },
      { id: 'r-021', type: 'CNAME', name: 'api',           value: 'api.erpcloud.aptean.com',ttl: 'auto',proxied: true  },
      { id: 'r-022', type: 'TXT',   name: '_verify',       value: 'aptean-verify=abc123',  ttl: 300,    proxied: false },
    ],
  },
  {
    id: 'zone-4',
    name: 'aptean-dev.app',
    status: 'pending',
    records: [
      { id: 'r-030', type: 'A',     name: '@',             value: '10.0.100.1',            ttl: 300,    proxied: false },
    ],
  },
]

const RECORD_TYPE_COLORS: Record<RecordType, string> = {
  A:     'var(--aptean-teal)',
  AAAA:  'var(--aptean-teal)',
  CNAME: 'var(--dc-ops)',
  MX:    'var(--dc-infra)',
  TXT:   'var(--aptean-warning)',
  NS:    'var(--aptean-text-muted)',
  SRV:   'var(--dc-observe)',
}

const RECORD_TYPE_BG: Record<RecordType, string> = {
  A:     'var(--aptean-teal-subtle)',
  AAAA:  'var(--aptean-teal-subtle)',
  CNAME: 'var(--dc-ops-bg)',
  MX:    'var(--dc-infra-bg)',
  TXT:   'var(--aptean-warning-bg)',
  NS:    'var(--aptean-bg-elevated)',
  SRV:   'var(--dc-observe-bg)',
}

export function CloudflarePage() {
  const [activeZone, setActiveZone] = useState(ZONES[0].id)
  const [showNewRecord, setShowNewRecord] = useState(false)
  const [hstsModeIdx, setHstsModeIdx] = useState(0)
  const [wafEnabled, setWafEnabled] = useState(true)

  const currentZone = ZONES.find((z) => z.id === activeZone) ?? ZONES[0]

  const totalRecords = ZONES.reduce((sum, z) => sum + z.records.length, 0)

  const sslModes = ['Flexible', 'Full', 'Full (Strict)']

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Globe size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Cloudflare Configuration</h1>
            <p className={styles.pageSubtitle}>Manage DNS records, SSL/TLS settings, and WAF rules across zones</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary}>
            <RefreshCw size={14} />
            Sync
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowNewRecord(true)}>
            <Plus size={15} />
            Add Record
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-infra-bg)', color: 'var(--dc-infra)' }}>
            <Globe size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{ZONES.length}</div>
            <div className={styles.statLabel}>Zones</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-deploy-bg)', color: 'var(--dc-deploy)' }}>
            <Wifi size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{totalRecords}</div>
            <div className={styles.statLabel}>DNS Records</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{ZONES.filter((z) => z.status === 'active').length} Active</div>
            <div className={styles.statLabel}>Zone Status</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <Shield size={18} />
          </div>
          <div>
            <div className={styles.statValue}>WAF Active</div>
            <div className={styles.statLabel}>Security</div>
          </div>
        </div>
      </div>

      {/* Zone Selector Tabs */}
      <div className={styles.zoneTabs}>
        {ZONES.map((zone) => (
          <button
            key={zone.id}
            className={`${styles.zoneTab} ${activeZone === zone.id ? styles.zoneTabActive : ''}`}
            onClick={() => setActiveZone(zone.id)}
          >
            <Globe size={13} />
            {zone.name}
            {zone.status === 'pending' && <AlertTriangle size={12} className={styles.zonePending} />}
            <span className={styles.zoneRecordCount}>{zone.records.length}</span>
          </button>
        ))}
      </div>

      {/* DNS Records Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.tableLeft}>
            <span className={styles.tableTitle}>DNS Records — {currentZone.name}</span>
            <span className={`${styles.zoneStatus} ${currentZone.status === 'active' ? styles.zoneStatusActive : styles.zoneStatusPending}`}>
              {currentZone.status}
            </span>
          </div>
          <span className={styles.recordCount}>{currentZone.records.length} records</span>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Value / Target</th>
                <th>TTL</th>
                <th>Proxied</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentZone.records.map((record) => (
                <tr key={record.id} className={styles.tableRow}>
                  <td>
                    <span
                      className={styles.recordType}
                      style={{ background: RECORD_TYPE_BG[record.type], color: RECORD_TYPE_COLORS[record.type] }}
                    >
                      {record.type}
                    </span>
                  </td>
                  <td className={styles.cellMono}>{record.name}</td>
                  <td className={`${styles.cellMono} ${styles.valueCell}`}>{record.value}</td>
                  <td className={styles.cellMuted}>{record.ttl === 'auto' ? 'Auto' : `${record.ttl}s`}</td>
                  <td>
                    <div className={`${styles.proxiedToggle} ${record.proxied ? styles.proxiedOn : styles.proxiedOff}`}>
                      <Wifi size={11} />
                      {record.proxied ? 'Proxied' : 'DNS only'}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actionRow}>
                      <button className={styles.actionBtn}><Edit2 size={12} /></button>
                      <button className={styles.actionBtnDanger}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SSL/TLS Settings Panel */}
      <div className={styles.settingsCard}>
        <div className={styles.settingsHeader}>
          <Key size={16} />
          <span>SSL / TLS Settings — {currentZone.name}</span>
        </div>
        <div className={styles.settingsBody}>
          <div className={styles.settingRow}>
            <div>
              <div className={styles.settingLabel}>Encryption Mode</div>
              <div className={styles.settingDesc}>Controls how Cloudflare connects to your origin server</div>
            </div>
            <div className={styles.settingControl}>
              <div className={styles.segmented}>
                {sslModes.map((mode, i) => (
                  <button
                    key={mode}
                    className={`${styles.segBtn} ${hstsModeIdx === i ? styles.segBtnActive : ''}`}
                    onClick={() => setHstsModeIdx(i)}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.settingRow}>
            <div>
              <div className={styles.settingLabel}>HSTS</div>
              <div className={styles.settingDesc}>Force HTTPS with HTTP Strict Transport Security</div>
            </div>
            <div className={styles.settingControl}>
              <button className={`${styles.toggle} ${styles.toggleOn}`}>
                <span className={styles.toggleKnob} />
              </button>
              <span className={styles.toggleLabel}>Enabled</span>
            </div>
          </div>
          <div className={styles.settingRow}>
            <div>
              <div className={styles.settingLabel}>Minimum TLS Version</div>
              <div className={styles.settingDesc}>Visitors using older TLS versions will be rejected</div>
            </div>
            <div className={styles.settingControl}>
              <select className={styles.settingSelect}>
                <option>TLS 1.2</option>
                <option>TLS 1.3</option>
              </select>
            </div>
          </div>
          <div className={styles.settingRow}>
            <div>
              <div className={styles.settingLabel}>Web Application Firewall</div>
              <div className={styles.settingDesc}>Block malicious traffic with Cloudflare WAF rules</div>
            </div>
            <div className={styles.settingControl}>
              <button
                className={`${styles.toggle} ${wafEnabled ? styles.toggleOn : styles.toggleOff}`}
                onClick={() => setWafEnabled((v) => !v)}
              >
                <span className={styles.toggleKnob} />
              </button>
              <span className={styles.toggleLabel}>{wafEnabled ? 'Active' : 'Disabled'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* New Record Modal */}
      {showNewRecord && (
        <div className={styles.modalOverlay} onClick={() => setShowNewRecord(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Add DNS Record</h2>
            <p className={styles.modalSubtitle}>Zone: {currentZone.name}</p>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Type</label>
                <select className={styles.formSelect}>
                  {(['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'] as RecordType[]).map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>TTL</label>
                <select className={styles.formSelect}>
                  <option value="auto">Auto</option>
                  <option value="300">5 min</option>
                  <option value="3600">1 hr</option>
                  <option value="86400">1 day</option>
                </select>
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Name</label>
                <input className={styles.formInput} placeholder="e.g. api or @ for root" />
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Value / Target</label>
                <input className={styles.formInput} placeholder="e.g. 20.42.100.10" />
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setShowNewRecord(false)}>Cancel</button>
              <button className={styles.btnPrimary} onClick={() => setShowNewRecord(false)}>Add Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
